import {Card, Checkbox, Choice} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {isNotEmpty} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {BulkPrintDocumentKey} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {DOT_CHARACTER, suffixTestId, type RequiredTestIdProps} from 'shared/web';

import type {DocumentSelection, TemplateOption} from '../hooks/useDocumentSelections';
import type {TemplateOptionGroup} from '../utils/groupTemplateOptionsByLocale';

interface DocumentRowProps extends RequiredTestIdProps {
  documentKey: BulkPrintDocumentKey;
  titleKey: string;
  selection: DocumentSelection;
  restrictionMessage: string[] | undefined;
  templateOptions: TemplateOption[] | TemplateOptionGroup[];
  isDisabled: boolean;
  onCheckboxChange: (documentKey: BulkPrintDocumentKey, isSelected: boolean) => void;
  onTemplateChange: (documentKey: BulkPrintDocumentKey, templateId: string | null | number) => void;
}

export function DocumentRow(props: DocumentRowProps) {
  const hasTemplates = props.templateOptions.length > 0;
  const hasRestrictionError =
    isNotNil(props.restrictionMessage) && isNotEmpty(props.restrictionMessage);

  return (
    <Card variant="inlineGrey">
      <VStack spacing={2}>
        <HStack spacing={3} align="center" justify="space-between">
          <HStack spacing={3} align="center">
            <Checkbox
              value={props.selection.isSelected}
              onChange={(value) => props.onCheckboxChange(props.documentKey, value)}
              isDisabled={props.isDisabled}
              data-testid={suffixTestId(`checkbox-${props.documentKey}`, props)}
            />
            <Heading size={4}>{i18n.t(props.titleKey)}</Heading>
          </HStack>

          <Show when={hasTemplates}>
            <Box width={52}>
              <Choice
                value={props.selection.templateId ?? null}
                onChange={(value) => props.onTemplateChange(props.documentKey, value)}
                options={props.templateOptions}
                isDisabled={props.isDisabled || !props.selection.isSelected}
                data-testid={suffixTestId(`template-${props.documentKey}`, props)}
              />
            </Box>
          </Show>
        </HStack>

        <Show when={hasRestrictionError}>
          <VStack
            spacing={1}
            data-testid={suffixTestId(`restrictionError-${props.documentKey}`, props)}
          >
            {props.restrictionMessage?.map((message) => (
              <HStack key={message} spacing={1} align="center">
                <Icon value="alert/error" size={4} color="severity.danger" />
                <Text size="small" color="danger">
                  {i18n.t('entity.order.documents.errorLabel')}
                </Text>
                <Text size="small" color="secondary">
                  {DOT_CHARACTER}
                </Text>
                <Text size="small" color="secondary">
                  {message}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Show>
      </VStack>
    </Card>
  );
}
